import { useCallback, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Assistant } from './components/Assistant';
import { Breadcrumbs } from './components/Breadcrumbs';
import { Topbar } from './components/Topbar';

import { ApiError, api, type Me } from './lib/api';
import { EnvPill } from './components/ui';
import { AcceptInvite } from './pages/AcceptInvite';
import { Login } from './pages/Login';
import { Docs } from './public/Docs';
import { Landing } from './public/Landing';
import { Pricing } from './public/Pricing';
import { Signup } from './pages/Signup';
import { Overview } from './pages/Overview';
import { Emails } from './pages/Emails';
import { EmailDetail } from './pages/EmailDetail';
import { Domains } from './pages/Domains';
import { ApiKeys } from './pages/ApiKeys';
import { Webhooks } from './pages/Webhooks';
import { Templates } from './pages/Templates';
import { Suppressions } from './pages/Suppressions';
import { AuditLog } from './pages/AuditLog';
import { Audiences } from './pages/Audiences';
import { Broadcasts } from './pages/Broadcasts';
import { Billing } from './pages/Billing';
import { Settings } from './pages/Settings';
import { Health } from './pages/Health';
import { Enterprise } from './pages/Enterprise';

const NAV = [
  { section: 'Monitor', items: [
    { to: '/', label: 'Overview', glyph: '◴' },
    { to: '/emails', label: 'Email logs', glyph: '✉' },
    { to: '/suppressions', label: 'Suppressions', glyph: '⊘' },
    { to: '/health', label: 'Health', glyph: '⏱' },
  ]},
  { section: 'Configure', items: [
    { to: '/domains', label: 'Domains', glyph: '⌾' },
    { to: '/api-keys', label: 'API keys', glyph: '⚿' },
    { to: '/webhooks', label: 'Webhooks', glyph: '↯' },
    { to: '/templates', label: 'Templates', glyph: '❐' },
  ]},
  { section: 'Grow', items: [
    { to: '/audiences', label: 'Audiences', glyph: '⚇' },
    { to: '/broadcasts', label: 'Broadcasts', glyph: '⇉' },
  ]},
  { section: 'Account', items: [
    { to: '/enterprise', label: 'Enterprise', glyph: '⛨' },
    { to: '/audit-log', label: 'Audit log', glyph: '☰' },
    { to: '/billing', label: 'Billing', glyph: '◫' },
    { to: '/settings', label: 'Settings', glyph: '⚙' },
  ]},
  // Opened in a new tab, both of them. Somebody reading how to send their first
  // email is copying values out of the dashboard while they do it, and replacing
  // the tab they were copying from is the one thing that makes docs annoying.
  { section: 'Developers', items: [
    { to: '/docs', label: 'Guides', glyph: '❑', newTab: true },
    // Not a SPA route: nginx proxies /reference to the API, which renders the
    // schema filtered to the customer-facing tags. An <a> rather than a NavLink,
    // or the router would try to own a URL it does not have a route for.
    { to: '/reference', label: 'API reference', glyph: '⌘', newTab: true },
  ]},
];

/** A persistent banner while an operator is viewing this dashboard (admin §9).
 *
 *  Not dismissible, on purpose. The point is that nobody can forget whose account
 *  they are looking at, and a banner that can be closed is a banner that gets
 *  closed. It states read-only too, so a refused button reads as expected rather
 *  than as the product being broken.
 */
function ImpersonationBanner({ info }: { info: NonNullable<Me['impersonation']> }) {
  return (
    <div className="impersonation-banner" role="status">
      <strong>Viewing as {info.tenant_name}</strong>
      <span>
        {info.admin_email} · read-only · {info.reason}
      </span>
    </div>
  );
}

/** Whether the sidebar starts collapsed.
 *
 *  Remembered per browser, because it is a working preference rather than a
 *  setting: somebody who collapses it wants it collapsed tomorrow, and asking
 *  the server to store that would be a migration for a boolean nobody else
 *  needs to see. Wrapped because storage throws outright in a private window
 *  and in some embedded views.
 */
function storedCollapsed(): boolean {
  try {
    return localStorage.getItem('mailgiri.sidebar') === 'collapsed';
  } catch {
    return false;
  }
}

export default function App() {
  const [collapsed, setCollapsed] = useState(storedCollapsed);
  const [me, setMe] = useState<Me | null>(null);
  // 'checking' keeps the login form from flashing before the cookie is resolved.
  const [state, setState] = useState<'checking' | 'ready'>('checking');
  const location = useLocation();

  const refresh = useCallback(async () => {
    try {
      setMe(await api.auth.me());
    } catch (error) {
      // A 401 here is the normal "not signed in" answer, not a failure.
      if (!(error instanceof ApiError && error.isUnauthorized)) {
        console.error('session check failed', error);
      }
      setMe(null);
    } finally {
      setState('ready');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (state === 'checking') {
    return (
      <div className="auth-wrap">
        <div className="subtitle">Loading…</div>
      </div>
    );
  }

  if (!me) {
    return (
      <Routes>
        {/* The public site. `/` is the marketing page now rather than the login
            form -- a logged-out visitor arriving at the root should be sold to,
            not challenged. */}
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/docs" element={<Docs />} />
        <Route path="/docs/:slug" element={<Docs />} />
        <Route path="/login" element={<Login onAuthenticated={refresh} />} />
        <Route path="/signup" element={<Signup onAuthenticated={refresh} />} />
        {/* Reachable without a session: the invitee has no account yet. */}
        <Route
          path="/accept-invite"
          element={<AcceptInvite onAuthenticated={refresh} />}
        />
        {/* Any other path is a dashboard deep link hit without a session, so it
            lands on the login form rather than the marketing page. The URL is
            kept as-is, which is the behaviour this had before. */}
        <Route path="*" element={<Login onAuthenticated={refresh} />} />
      </Routes>
    );
  }

  return (
    <>
      {me.impersonation && <ImpersonationBanner info={me.impersonation} />}
      <div className={collapsed ? 'shell collapsed' : 'shell'}>
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">m</span>
          mailgiri
        </div>

        {NAV.map((group) => (
          <div key={group.section}>
            <div className="nav-section">{group.section}</div>
            {group.items.map((item) =>
              'newTab' in item && item.newTab ? (
                <a
                  key={item.to}
                  href={item.to}
                  target="_blank"
                  rel="noreferrer"
                  className="nav-link"
                >
                  <span className="glyph" aria-hidden="true">{item.glyph}</span>
                  <span className="nav-label">
                    {item.label}
                    {/* Announced, because "opens in a new tab" is information a
                        sighted user gets from the arrow and nobody else does. */}
                    <span className="sr-only"> (opens in a new tab)</span>
                  </span>
                  <span className="nav-external" aria-hidden="true">↗</span>
                </a>
              ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
              >
                <span className="glyph" aria-hidden="true">{item.glyph}</span>
                {/* Wrapped, not a bare text node. A text node cannot be selected
                    by CSS, so the collapsed rule that hides the label had nothing
                    to match and the label stayed -- squeezing the glyph out of a
                    64px rail. */}
                <span className="nav-label">{item.label}</span>
              </NavLink>
              ),
            )}
          </div>
        ))}

        <div className="sidebar-footer">
          {/* Identity and sign-out moved to the top bar, where the layout this
              follows puts them. What stays is the one thing somebody checks
              before acting: which domain their mail leaves from. */}
          <div className="mono" style={{ fontSize: 11.5 }}>
            {me.tenant.sending_domain}
          </div>
          <div className="row" style={{ marginTop: 8, gap: 6 }}>
            <EnvPill
              environment={me.tenant.status === 'active' ? 'live' : me.tenant.status}
            />
          </div>
        </div>
      </aside>

      <div className="main-col">
        <Topbar
          me={me}
          nav={NAV}
          collapsed={collapsed}
          onToggleSidebar={() => {
            const next = !collapsed;
            setCollapsed(next);
            try {
              localStorage.setItem('mailgiri.sidebar', next ? 'collapsed' : 'open');
            } catch {
              // A remembered preference is a convenience, not the mechanism.
            }
          }}
          onSignedOut={refresh}
        />

        <main className="main" key={location.pathname}>
        <Breadcrumbs nav={NAV} />
        <Routes>
          <Route path="/" element={<Overview me={me} />} />
          <Route path="/emails" element={<Emails />} />
          <Route path="/emails/:messageId" element={<EmailDetail />} />
          <Route path="/domains" element={<Domains me={me} />} />
          <Route path="/api-keys" element={<ApiKeys />} />
          <Route path="/webhooks" element={<Webhooks />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/suppressions" element={<Suppressions />} />
          <Route path="/audiences" element={<Audiences />} />
          <Route
            path="/broadcasts"
            element={<Broadcasts sendingDomain={me.tenant.sending_domain} />}
          />
          <Route path="/health" element={<Health />} />
          <Route path="/enterprise" element={<Enterprise me={me} />} />
          <Route path="/audit-log" element={<AuditLog />} />
          <Route path="/billing" element={<Billing me={me} />} />
          <Route path="/settings" element={<Settings me={me} onChanged={refresh} />} />
          <Route
            path="/accept-invite"
            element={<AcceptInvite onAuthenticated={refresh} />}
          />
          {/* The guides, reachable signed in or out. Rendered in the public
              shell, which is a deliberate difference: docs are read alongside an
              editor, not inside an admin chrome. */}
          <Route path="/docs" element={<Docs />} />
          <Route path="/docs/:slug" element={<Docs />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </main>

        <footer className="app-footer">
          <span>© {new Date().getFullYear()} mailgiri</span>
          <span className="mono">{me.tenant.sending_domain}</span>
        </footer>
      </div>
      </div>
      {/* Outside <main>, because it is not part of the page: it floats over
          whatever screen you are on, and its state survives navigation. Renders
          nothing at all when no model is configured. */}
      <Assistant />
    </>
  );
}
